import type { TFunction } from "i18next";
import { isAxiosError } from "axios";
import { formatExplorerLimitResetUtc } from "./formatExplorerLimitResetUtc";
import {
  getExplorerLimitErrorWeekResetsUtc,
  type ExplorerConnectionLimitErrorBody,
} from "./explorerConnectionLimitError";

export type ExplorerLimitCopyOptions = {
  locale: string;
  weekResetsUtc?: string;
  used?: number;
  limit?: number;
};

export function getExplorerWeeklyResetLabel(
  t: TFunction,
  locale: string,
  weekResetsUtc?: string
): string {
  const when = formatExplorerLimitResetUtc(weekResetsUtc, locale);
  if (!when) {
    return t("connections.explorerWeeklyResetFallback");
  }
  return t("connections.explorerWeeklyResetAt", { when });
}

export function getExplorerLimitReachedMessage(
  t: TFunction,
  options: ExplorerLimitCopyOptions
): string {
  const { locale, weekResetsUtc, used = 1, limit = 1 } = options;
  return t("connections.explorerWeeklyLimitReached", {
    used,
    limit,
    resetLabel: getExplorerWeeklyResetLabel(t, locale, weekResetsUtc),
  });
}

export function getExplorerLimitCounterMessage(
  t: TFunction,
  options: ExplorerLimitCopyOptions
): string {
  const { locale, weekResetsUtc, used = 0, limit = 1 } = options;
  return t("connections.explorerWeeklyCounter", {
    used,
    limit,
    remaining: Math.max(0, limit - used),
    resetLabel: getExplorerWeeklyResetLabel(t, locale, weekResetsUtc),
  });
}

export function getExplorerLimitMessageFromError(
  t: TFunction,
  error: unknown,
  locale: string,
  fallback?: ExplorerLimitCopyOptions
): string {
  const data = isAxiosError(error)
    ? (error.response?.data as ExplorerConnectionLimitErrorBody | undefined)
    : undefined;
  return getExplorerLimitReachedMessage(t, {
    locale,
    weekResetsUtc:
      getExplorerLimitErrorWeekResetsUtc(error) ?? fallback?.weekResetsUtc,
    used: data?.used ?? fallback?.used,
    limit: data?.limit ?? fallback?.limit,
  });
}
